import { Check } from "lucide-react";
import { StatusBadge } from "@/domains/pm/components/shared/StatusBadge";
import { FittedDisciplineBadges } from "@/domains/pm/components/shared/FittedDisciplineBadges";
import { formatDateOnly } from "@/domains/pm/utils/protocol";
import { isIssueOverdue } from "@/domains/pm/utils/dueDate";
import { fill } from "@/lib/i18n/fill";
import { cn } from "@/lib/utils";
import type { Language, Translations } from "@/lib/i18n/types";
import type { PMIssue } from "@/domains/pm/types";

type ProtocolCopy = Translations["pm"]["issues"]["protocol"];

interface ProtocolIssueRowProps {
  issue: PMIssue;
  included: boolean;
  language: Language;
  copy: ProtocolCopy;
  /** Shift extends the range from the anchor — the tick cell only. */
  onToggle: (id: number, shiftKey: boolean) => void;
  /** Anywhere else on the row: open the full issue. */
  onOpen: (id: number) => void;
}

/** What both cells below need to name themselves in their aria labels. A type
 *  alias, not an interface: `fill` takes a `Record<string, string>`, and only an
 *  alias gets the implicit index signature that makes one assignable. */
type IssueNames = { number: string; title: string };

/**
 * The tick cell, whose whole area is the hit target — as it is in the issues
 * list.
 *
 * The box inside it is a real `<button>` carrying the role and the name rather
 * than the list's `pointer-events-none readOnly` input: that input is focusable
 * but unclickable and `readOnly` does nothing on a checkbox, so Space flips the
 * box while the cell's handler never fires and the two disagree. This button has
 * no click of its own — the event bubbles to the cell — so mouse and keyboard
 * take one path.
 */
function IncludeCell({
  included,
  names,
  copy,
  onToggle,
}: {
  included: boolean;
  names: IssueNames;
  copy: ProtocolCopy;
  /** Shift extends the range from the anchor — see `ProtocolIssueRowProps`. */
  onToggle: (shiftKey: boolean) => void;
}) {
  return (
    <td
      className="w-8 px-3 py-2.5"
      onClick={(event) => {
        event.stopPropagation();
        onToggle(event.shiftKey);
      }}
    >
      <button
        type="button"
        role="checkbox"
        aria-checked={included}
        aria-label={fill(copy.includeIssueAria, names)}
        className="flex items-center rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      >
        <span
          aria-hidden
          className={cn(
            "flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
            included && "bg-primary text-primary-foreground"
          )}
        >
          {included && <Check className="h-4 w-4" />}
        </span>
      </button>
    </td>
  );
}

/**
 * The number and title cell — and the keyboard path to the issue. The row's own
 * click is a mouse convenience on top of it, so neither is the only way in.
 */
function TitleCell({
  names,
  copy,
  onOpen,
}: {
  names: IssueNames;
  copy: ProtocolCopy;
  onOpen: () => void;
}) {
  return (
    <td className="px-3 py-2.5">
      <button
        type="button"
        aria-label={fill(copy.openIssueAria, names)}
        className="flex w-full items-center gap-2 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        onClick={(event) => {
          event.stopPropagation();
          onOpen();
        }}
      >
        <span className="shrink-0 font-mono text-xs text-muted-foreground">#{names.number}</span>
        <span className="min-w-0 flex-1 truncate text-sm">{names.title}</span>
      </button>
    </td>
  );
}

/**
 * One issue in the protocol picker, as a row of the same table the issues list
 * draws — same column rhythm, same hover, same hairline between rows.
 */
export function ProtocolIssueRow({
  issue,
  included,
  language,
  copy,
  onToggle,
  onOpen,
}: ProtocolIssueRowProps) {
  const disciplines = issue.disciplines ?? [];
  const overdue = isIssueOverdue(issue);
  const names = { number: String(issue.issue_number), title: issue.title };

  return (
    <tr
      className="cursor-pointer border-b transition-colors hover:bg-[#f7f7f7]"
      onClick={() => onOpen(issue.id)}
    >
      <IncludeCell
        included={included}
        names={names}
        copy={copy}
        onToggle={(shiftKey) => onToggle(issue.id, shiftKey)}
      />

      <TitleCell names={names} copy={copy} onOpen={() => onOpen(issue.id)} />

      <td className="px-3 py-2.5">
        <StatusBadge status={issue.status} />
      </td>

      <td className="px-3 py-2.5">
        <FittedDisciplineBadges disciplines={disciplines} size="sm" variant="outline" />
      </td>

      <td
        className={cn(
          "whitespace-nowrap px-3 py-2.5 text-xs",
          overdue ? "text-destructive" : "text-muted-foreground"
        )}
      >
        {issue.due_date ? formatDateOnly(issue.due_date, language) : ""}
      </td>
    </tr>
  );
}
